import React, { useMemo, useState } from "react";
import "./SuccessorsList.scss";
import Table from "rc-table";
import { isDesktop } from "utils";
import { SVG_ENUM } from "enums";
import { Button, EmptyState } from "shared";
import { useGetSuccessors } from "hooks/successors";
import { desktopColumns, mobileColumns } from "./utilsSuccessorsList";
import { SuccessorType } from "types/SuccessorTypes";

const MobileDocDetails: React.FC<{ record: SuccessorType }> = ({ record }) => {
  return (
    <div className="doc-details px-4 py-3">
      <div className="doc-details__row">
        <span>Pesel:</span>
        <strong>{record.nationalId}</strong>
      </div>
      <div className="doc-details__row">
        <span>Telefon:</span>
        <strong>{record.phone}</strong>
      </div>
      <div className="doc-details__row">
        <span>E-mail:</span>
        <strong>{record.email}</strong>
      </div>
      <div className="doc-details__row">
        <span>Adres:</span>
        <strong>
          {record.address}, {record.postalCode} {record.city}
        </strong>
      </div>
    </div>
  );
};

interface SuccessorsListType {
  setAddSuccessor: () => void;
  setDeleteSuccessor: (successor: SuccessorType) => void;
  setFinalizePayment: (successor: SuccessorType) => void;
}

const SuccessorsList: React.FC<SuccessorsListType> = ({
  setAddSuccessor,
  setDeleteSuccessor,
  setFinalizePayment,
}) => {
  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);
  const { data } = useGetSuccessors({ enabled: true });
  const notEmpty = !!(data && data.length > 0);
  const desktop = isDesktop();
  const rowKeyFn = (r: SuccessorType) => r.uuid;

  const columns = useMemo(
    () =>
      desktop
        ? desktopColumns({
            setShowDelete: (successor: SuccessorType) =>
              setDeleteSuccessor(successor),
            setFinalizePayment: (successor: SuccessorType) =>
              setFinalizePayment(successor),
          })
        : mobileColumns({
            setShowDelete: (successor: SuccessorType) =>
              setDeleteSuccessor(successor),
            setFinalizePayment: (successor: SuccessorType) =>
              setFinalizePayment(successor),
          }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [desktop, setDeleteSuccessor]
  );

  return (
    <div className="documents-list">
      {notEmpty ? (
        <>
          <Button
            className="button--rounded button--black"
            size="medium"
            type="button"
            label="Dodaj sukcesora +"
            onClick={() => setAddSuccessor()}
          />
          <div className="documents-list__header">Uprawnione kontakty</div>
          <Table
            rowKey={rowKeyFn}
            className="companies-table documents-table"
            data={data}
            columns={columns}
            onRow={(record: SuccessorType) => {
              const id = rowKeyFn(record);
              const isActive = expandedRowKeys.includes(id);
              return {
                className: isActive ? "active-row" : "",
              };
            }}
            expandable={
              desktop
                ? undefined
                : {
                    expandedRowRender: (record: SuccessorType) => (
                      <MobileDocDetails record={record} />
                    ),
                    rowExpandable: () => true,
                    expandRowByClick: true,
                    expandedRowKeys,
                    onExpand: (expanded, record: SuccessorType) => {
                      const id = rowKeyFn(record);
                      setExpandedRowKeys((prev) =>
                        expanded ? [id] : prev.filter((k) => k !== id)
                      );
                    },
                  }
            }
          />
        </>
      ) : (
        <EmptyState
          className="successor-page__empty-state"
          svg={SVG_ENUM.INFO}
          text={
            <p>
              W tym miejscu pojawi się lista kontaktów uprawnionych do uzyskania
              informacji z zakładki “Sukcesja”. Aby dodać kontakt należy kliknąć
              przycisk <strong>“Dodaj kontakt”</strong> w prawym górnym rogu.
            </p>
          }
        />
      )}
    </div>
  );
};

export default SuccessorsList;
